import { Component, Inject, NgZone, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TagBloc } from '@kbloc';
import { EnvironmentVariablesService } from '@kenv';
import { environment } from '@kp/../environments/environment';
import { AuthenticationBloc } from '@kp/auth/authentication.bloc';
import { DataLinkBloc } from '@kp/data-link/data-link.bloc';
import { DialogueBloc } from '@kp/dialogue/dialogue.bloc';
import { DialogueBlockType } from '@kp/dialogue/models/dialogue-block.model';
import { PulseSurveyBloc } from '@kp/pulse-survey/pulse-survey.bloc';
import { Constants } from '@kp/shared/constants.service';
import { UserBloc } from '@kp/user/user.bloc';
import {
  AuthDataService,
  DataStoreService,
  OnboardingUtilities,
  TitleService,
  WINDOW,
  productDisplayName,
} from '@kservice';
import { Product } from '@ktypes/enums';
import { AuthData, AuthProviderState, SOCIAL_SSO, Status } from '@ktypes/models';
import { Popup } from '@kutil';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'kp-login-choice',
  templateUrl: './login-choice.component.html',
  styleUrls: ['./login-choice.component.scss'],
})
export class LoginChoiceComponent implements OnInit, OnDestroy {
  loginDisabled: boolean;
  product = Product;
  productName = productDisplayName;
  socialSso = SOCIAL_SSO;
  redirectUrl: string;

  private _destroy$ = new Subject<void>();
  private _loginError: boolean;
  private _popup: Popup;
  private _title = 'Login';

  constructor(
    private _activatedRoute: ActivatedRoute,
    private _authDataService: AuthDataService,
    private _authenticationBloc: AuthenticationBloc,
    private _dataStoreService: DataStoreService,
    private _dataLinkBloc: DataLinkBloc,
    private _dialogueBloc: DialogueBloc,
    public envVariables: EnvironmentVariablesService,
    private _ngZone: NgZone,
    private _onboardingUtil: OnboardingUtilities,
    private _popupService: Popup,
    private _pulseSurveyBloc: PulseSurveyBloc,
    private _router: Router,
    private _tagBloc: TagBloc,
    private _titleService: TitleService,
    private _userBloc: UserBloc,
    @Inject(WINDOW) private _window: Window
  ) {}

  ngOnInit() {
    this._titleService.set(this._title);
    const { queryParams } = this._activatedRoute.snapshot;
    this.redirectUrl = queryParams['redirectUrl'];

    this._dialogueBloc.currentDialogueBlock$.pipe(takeUntil(this._destroy$)).subscribe((currentDialogueBlock) => {
      if (currentDialogueBlock?.type === DialogueBlockType.dialogue_block_signup_sequence) {
        // user was in the signup sequence and chose to go to login,
        // terminate Dialogue and any account information
        this._authenticationBloc.clearAuthData();
        this._onboardingUtil.cleanupOnboardingStorage();
        this._dialogueBloc.clearDialogueStreams();
      }
    });
  }

  ngOnDestroy() {
    this._destroy$.next();
    this._destroy$.complete();
  }

  ssoLogin(provider: SOCIAL_SSO) {
    this._authenticationBloc.clearAuthData();
    this._dataLinkBloc.clearLinkData();
    this._window['kpUpdateUser'] = this.updateUser.bind(this);
    this._popup = this._popupService.initialize({
      url: 'about:blank',
      name: 'ssologin',
      windowFeatures: {
        width: 640,
        height: 680,
      },
      onClose: this.onPopupClose.bind(this),
    });
    this._loginError = false;
    this.loginDisabled = true;

    // Make sure the user is logged out
    if (this._popup) {
      let url = Constants.ssoLogoutUrlTemplate
        .replace('$authHost', environment.oauth.domain)
        .replace('$clientId', environment.oauth.authClientSocialId)
        .replace('$kumanuLogout', encodeURIComponent(environment.oauth.redirectSignOut));

      if (provider === SOCIAL_SSO.Google) {
        // Make sure the user is logged out if Google SSO
        this._popup.change_url(url);
      }

      // setup URL for SSO login
      url = Constants.ssoLoginUrlTemplate
        .replace('$authHost', environment.oauth.domain)
        .replace('$provider', provider)
        .replace('$kumanuHost', encodeURIComponent(environment.oauth.redirectSignIn))
        .replace('$response_type', environment.oauth.responseType)
        .replace('$clientId', environment.oauth.authClientSocialId)
        .replace('$state', new AuthProviderState(provider, null, null, this.envVariables.product as Product).asBase64);

      if (provider === SOCIAL_SSO.Google) {
        this.checkPopupUrl(url);
      } else {
        this._popup.change_url(url);
      }
    }

    // Re-enable login buttons after 1s
    setTimeout(() => (this.loginDisabled = false), 1000);
  }

  private checkPopupUrl(url: string) {
    // after 4s, head to login regardless
    const failsafeTimer = setTimeout(() => {
      this._popup.replace_url(url);
      this._popup.focus();
    }, 4000);

    if (this._popup.popupWin.location?.href?.includes(environment.oauth.redirectSignOut)) {
      clearTimeout(failsafeTimer);
      this._popup.replace_url(url);
      this._popup.focus();
      return;
    }

    // check for url change and close popup
    setTimeout(() => {
      this.checkPopupUrl(url);
    }, 250);
  }

  private onPopupClose() {
    this._loginError = false;
    this.loginDisabled = false;
  }

  async updateUser({ token, refreshToken }) {
    return new Promise((resolve, reject) => {
      // Save token information to authData
      const authData = new AuthData().deserialize({ token, refreshToken });
      this._authDataService.updateToken(authData);

      // setup subscription to listen for login status to be done or error
      this._dataStoreService.authStatus$.pipe(takeUntil(this._destroy$)).subscribe((loginStatus) => {
        if (loginStatus != null) {
          if (loginStatus.data != null) {
            this._dataStoreService.setAuthData(loginStatus);
          }
          if (loginStatus.status === Status.done) {
            this._advanceToNextScreen();
            resolve(true);
          } else if (loginStatus.status === Status.error) {
            reject({ error: 'LOGIN_ERROR', message: 'Error during login' });
          }
        }
      });

      // fire off refresh token request, include user details
      this._userBloc.refreshUserFromToken(reject.bind({ error: 'LOGIN_ERROR', message: 'Error during login' }));
    });
  }

  private _advanceToNextScreen() {
    // clean up localStorage re: onboarding
    this._onboardingUtil.removeOnboardingData();

    // prep for and advance to the next page
    this._pulseSurveyBloc.allowPulseSurveyCheckToday(true);
    this._tagBloc.getUserTags();
    // Move to initial app screen
    // Use ngZone because this is outsize the global Zone
    const redirectRoute = (this.redirectUrl && decodeURIComponent(this.redirectUrl)) || '';
    this._ngZone.run(() => this._router.navigateByUrl(redirectRoute, { replaceUrl: true }));
  }
}
